import React, { useEffect, useState } from 'react';
import { useCurrentUserSelector } from '@entities/current-user/model/selectors';
import { CardAccountInfo } from '@entities/current-user/ui/card-account-info/card-account-info';
import { useUserApiPrefetch } from '@entities/user/api/user-management-api';
import { DEFAULT_USERS_LIST_PARAMETERS } from '@entities/user/config/store-constants';
import { usePrefetchOrganization } from '@entities/organization/api/organization-api';
import { userName } from '@shared/lib/utils/user-name/user-name';
import { FinderFeedbackRequest } from '@widgets/finder-feedback-request/ui/finder-feedback-request';
import { useFeedbackRequestListSharedQuery } from '@entities/feedback-request/libs/hooks/use-feedback-request-list-shared-query/use-feedback-request-list-shared-query';
import { FeedbackRequestListDataRequest } from '@entities/feedback-request/api/feedback-request-types';
import { DEFAULT_FEEDBACK_REQUESTS_LIST_PARAMETERS } from '@entities/feedback-request/config/store-constants';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import { CardFeedbackRequest } from '@entities/feedback-request/ui/card-feedback-request/card-feedback-request';
import { FeedbackRequestChangeStatus } from '@features/feedback-request/feedback-request-change-status/ui/feedback-request-change-status';
import { StyledNetworkStateNew } from './main-view-admin.styles';
import { Navigation } from '@shared/ui/navigation/navigation';
import { ProfilePhotoEdit } from '@features/current-user/edit-profile/ui/profile-photo-edit/profile-photo-edit';
import { ModalFeedbackRequestView } from '@features/feedback-request/feedback-request-change-status/ui/modal-feedback-request-view/modal-feedback-request-view';
import { useFeedbackRequestDetailSharedQuery } from '@entities/feedback-request/libs/hooks/use-feedback-request-detail-shared-query/use-feedback-request-detail-shared-query';

export const MainViewAdmin: React.FC = () => {
    const user = useCurrentUserSelector();
    const [isFeedbackRequestView, setIsFeedbackRequestView] = useState<number>();
    const [getFeedbackRequestDetail] = useFeedbackRequestDetailSharedQuery();
    const prefetchUsersList = useUserApiPrefetch('usersList', { ifOlderThan: 60 });
    const prefetchOrganizationList = usePrefetchOrganization('organizationsList', { ifOlderThan: 60 });
    const [feedbackRequestsListParameters, setFeedbackRequestsListParameters] =
        useState<FeedbackRequestListDataRequest>({
            ...DEFAULT_FEEDBACK_REQUESTS_LIST_PARAMETERS,
            take: 4,
            statuses: 'created,progress',
        });
    const [getFeedbackRequestsList, getFeedbackRequestsListResult] =
        useFeedbackRequestListSharedQuery(feedbackRequestsListParameters);

    const isFeedbackRequestsLoading =
        getFeedbackRequestsListResult.isLoading ||
        getFeedbackRequestsListResult.isFetching ||
        getFeedbackRequestsListResult.isUninitialized;

    useEffect(() => {
        prefetchUsersList(DEFAULT_USERS_LIST_PARAMETERS);
        prefetchOrganizationList(DEFAULT_USERS_LIST_PARAMETERS);
    }, []);

    useEffect(() => {
        getFeedbackRequestsList(feedbackRequestsListParameters);
    }, [feedbackRequestsListParameters]);

    return (
        <>
            <CardAccountInfo
                avatarUrl={user?.photoUrl || ''}
                name={userName({ ...user }).full}
                role={user?.role.roleName || ''}
                department={user?.department?.name || ''}
                showEditButton={true}>
                <ProfilePhotoEdit isEditMode={false} />
            </CardAccountInfo>
            <Navigation />
            <FinderFeedbackRequest
                feedbackRequestsListParameters={feedbackRequestsListParameters}
                setFeedbackRequestsListParameters={setFeedbackRequestsListParameters}
            />
            <StyledNetworkStateNew
                isLoading={isFeedbackRequestsLoading}
                isError={getFeedbackRequestsListResult.isError}
                isInitialized={Boolean(getFeedbackRequestsListResult.data)}
                isFiltered={Boolean(getFeedbackRequestsListResult.data?.search)}
                hasData={Boolean(getFeedbackRequestsListResult.data?.items.length)}
                errorProps={{
                    title: 'Ошибка загрузки обращений',
                    description: getQueryError(getFeedbackRequestsListResult)?.message,
                }}
                emptyProps={{
                    title: 'Обращения отсутствуют',
                }}
                emptyFilteredProps={{
                    title: 'Обращения не найдены',
                }}
                updateLoaderProps={{
                    style: { left: 20, right: 20 },
                }}>
                {getFeedbackRequestsListResult.data?.items.map((feedbackRequest) => (
                    <CardFeedbackRequest
                        key={feedbackRequest.id}
                        feedbackRequest={feedbackRequest}
                        onClick={() => setIsFeedbackRequestView(feedbackRequest.id)}
                        onMouseDown={() => getFeedbackRequestDetail(feedbackRequest.id)}>
                        <FeedbackRequestChangeStatus feedbackRequest={feedbackRequest} />
                    </CardFeedbackRequest>
                ))}
            </StyledNetworkStateNew>
            <ModalFeedbackRequestView
                isShow={Boolean(isFeedbackRequestView)}
                feedbackRequestId={isFeedbackRequestView}
                onClose={() => setIsFeedbackRequestView(0)}
            />
        </>
    );
};
